import React, { useState, useEffect } from 'react';
import { Modal } from './Modal';
import { THEME_OPTIONS, setThemePreference } from '../utils/theme';
import { useThemePreference } from '../hooks/useTheme';

const MAX_LOGO_BYTES = 512 * 1024;

export const SettingsModal = ({ isOpen, onClose, onLogoChange }) => {
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoError, setLogoError] = useState('');
  const themePreference = useThemePreference();

  // Re-read the saved logo each time the modal opens
  useEffect(() => {
    if (!isOpen) return;

    try {
      const savedLogo = localStorage.getItem('customLogo');
      setLogoPreview(savedLogo || null);
    } catch (e) {
      // ignore
    }

    setLogoError('');
  }, [isOpen]);

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    // Allow picking the same file again after an error.
    e.target.value = '';
    if (!file) return;

    if (!file.type || !file.type.startsWith('image/')) {
      setLogoError('Please choose an image file (PNG or JPG).');
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setLogoError('That image is too large. Please choose one smaller than 500 KB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64String = event.target.result;
      try {
        localStorage.setItem('customLogo', base64String);
      } catch (err) {
        setLogoError('The logo could not be saved in this browser. Try a smaller image.');
        return;
      }
      setLogoError('');
      setLogoPreview(base64String);
      if (onLogoChange) onLogoChange(base64String);
    };
    reader.onerror = () => setLogoError('The image could not be read. Please try another file.');
    reader.readAsDataURL(file);
  };

  const handleResetLogo = () => {
    try { localStorage.removeItem('customLogo'); } catch (e) {}
    setLogoPreview(null);
    setLogoError('');
    if (onLogoChange) onLogoChange(null);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Settings"
      footer={
        <button type="button" className="btn-secondary" onClick={onClose}>
          Close
        </button>
      }
    >
      <div className="settings-section">
        <h3>Appearance</h3>
        <p className="settings-description">
          Choose a light or dark theme, or follow your device setting.
        </p>
        <div className="segmented" role="group" aria-label="Theme">
          {THEME_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={option.value === themePreference ? 'segment is-active' : 'segment'}
              aria-pressed={option.value === themePreference}
              onClick={() => setThemePreference(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="settings-section">
        <h3>Dashboard Logo</h3>
        <p className="settings-description">
          Upload a custom logo image (recommended size: 60x60px)
        </p>

        {logoPreview && (
          <div className="logo-preview">
            <img src={logoPreview} alt="Logo Preview" style={{ maxWidth: '120px', maxHeight: '120px' }} />
            <p>Current Logo</p>
          </div>
        )}

        <div className="settings-actions">
          <label className="upload-btn">
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="visually-hidden"
              aria-describedby={logoError ? 'logo-error' : undefined}
            />
            Upload Image
          </label>

          {logoPreview && (
            <button type="button" className="btn-secondary" onClick={handleResetLogo}>
              Reset to Default
            </button>
          )}
        </div>

        {logoError && <span id="logo-error" className="field-error" role="alert">{logoError}</span>}

        <div className="settings-info">
          <p>
            Tips:
            <br />
            • Use PNG or JPG format, up to 500 KB
            <br />
            • Square images work best (e.g., 200x200px)
            <br />
            • Larger images will be scaled down to fit
            <br />
            • Changes are saved automatically in your browser
          </p>
        </div>
      </div>
    </Modal>
  );
};
